import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DataSetsRecordButtons from 'components/DataSetsRecordButtons/DataSetsRecordButtons';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import Panel from 'components/Panel/Panel';
import ProfitCenterLinksProduct from 'components/ProfitCenterLinksProduct/ProfitCenterLinksProduct';
import Separator from 'components/Separator/Separator';
import { PRODUCT_TYPE } from 'constants/formFields';
import { VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import UnitRefsProvider from 'contexts/UnitRefsContext';
import { getRelationTypeByProductType } from 'utils/formHelpers/productTypeValueChain';

import ProductGeneralFields from './components/ProductGeneralFields';
import ProductUnitLinksField from './components/ProductUnitLinksField';

import type { ICreateProductView } from './interfaces/ICreateProductView';

import styles from './sass/CreateProductView.module.scss';

function CreateProductView({
  control, onProductSubmit, isConfirmationModalOpen, onHandleCloseConfirmation,
  onDiscardChangesConfirm, enumData, isPageLoading,
  dateOpened, onDateOpenedChange, profitCenterIds, watch, setValue,
  handleGetVcOptions, vcOptionsList, listPosition, productCategoriesOptions,
}: ICreateProductView) {
  const { t } = useTranslation();

  const { recordId = '' } = useParams();
  const placeholder = recordId ? t('common.notProvided') : '';
  // Own-operations products link to balance sheet assets per profit centre instead, so the
  // product-level section is for supplier-sourced products only.
  const isSupplierSourced = getRelationTypeByProductType(watch(PRODUCT_TYPE)?.value)
    === VALUE_CHAIN_UNIT_TYPES.SUPPLIER;

  return (
    <UnitRefsProvider>
      <div className={styles.root}>
        <Panel className={styles.panel}>
          <div className={styles.wrapper}>
            <Loader isPageLoading={isPageLoading} isFullLoader={false} />
            <ProductGeneralFields
              control={control}
              enumData={enumData}
              dateOpened={dateOpened}
              onDateOpenedChange={onDateOpenedChange}
              recordId={recordId}
              handleGetVcOptions={handleGetVcOptions}
              vcOptionsList={vcOptionsList}
              listPosition={listPosition}
              setValue={setValue}
              productCategoriesOptions={productCategoriesOptions}
            />
            {isSupplierSourced ? (
              <DataSetsPanel title={t('products.selectionsSourcing')} className={styles.fullWidth}>
                <ProductUnitLinksField
                  control={control}
                  relationType={VALUE_CHAIN_UNIT_TYPES.SUPPLIER}
                />
              </DataSetsPanel>
            ) : null}
            <DataSetsPanel title={t('products.selectionsFinancials')} className={styles.fullWidth}>
              <ProfitCenterLinksProduct
                control={control}
                profitCenterIds={profitCenterIds}
                placeholder={placeholder}
                watch={watch}
                setValue={setValue}
                isManufacturedByVisible
              />
            </DataSetsPanel>
          </div>
          <Separator />
          <Modal
            open={isConfirmationModalOpen}
            title={t('createNewUser.cancelModalTitle')}
            description={t('clients.cancelModalText')}
            confirmTitle={t('common.discardChanges')}
            closeTitle={t('common.keepEditing')}
            confirmVariant="error"
            onClose={() => onHandleCloseConfirmation(false)}
            onReject={() => onHandleCloseConfirmation(false)}
            onConfirm={onDiscardChangesConfirm}
            isLoading={isPageLoading}
          />
          <DataSetsRecordButtons
            onClose={onHandleCloseConfirmation}
            onSubmit={onProductSubmit}
            isLoading={isPageLoading}
          />
        </Panel>
      </div>
    </UnitRefsProvider>
  );
}

export default CreateProductView;
